import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BookOpen, Calendar, ChevronDown, ChevronUp, RefreshCw, Sparkles, Check, Circle } from 'lucide-react'
import { vocabularyApi, type WordJourney } from '@/api/vocabulary'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Link } from 'react-router-dom'
import { cn } from '@/components/ui/cn'

interface WordJourneyPanelProps {
  userWordId: string
  defaultCollapsed?: boolean
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function sourceLabel(source: WordJourney['firstSeenSource']) {
  switch (source) {
    case 'ARTICLE':
      return 'while reading'
    case 'AI':
      return 'from practice'
    case 'IMPORT':
      return 'via import'
    default:
      return 'manually'
  }
}

function buildJourneySummary(journey: WordJourney) {
  const parts = [`Met ${formatDate(journey.firstSeenAt)}`, sourceLabel(journey.firstSeenSource)]
  if (journey.encounterCount > 0) {
    parts.push(`Seen ${journey.encounterCount}×`)
  }
  if (journey.reviewStats.total > 0) {
    parts.push(`Refreshed ${journey.reviewStats.total}×`)
  }
  parts.push(
    journey.nextReviewAt
      ? `Next ${formatDate(journey.nextReviewAt)}`
      : 'Not scheduled yet',
  )
  return parts.join(' · ')
}

export function WordJourneyPanel({ userWordId, defaultCollapsed = true }: WordJourneyPanelProps) {
  const [expanded, setExpanded] = useState(!defaultCollapsed)
  const { data: journey, isLoading } = useQuery({
    queryKey: ['wordJourney', userWordId],
    queryFn: () => vocabularyApi.getJourney(userWordId).then((r) => r.data),
  })
  const { data: formStats } = useQuery({
    queryKey: ['wordFormStats', userWordId],
    queryFn: () => vocabularyApi.getFormStats(userWordId).then((r) => r.data),
    enabled: expanded,
  })

  if (isLoading) {
    return (
      <div className="mb-6 flex h-24 items-center justify-center rounded-xl border bg-card">
        <LoadingSpinner />
      </div>
    )
  }

  if (!journey) return null

  const accuracy =
    journey.reviewStats.total > 0
      ? Math.round((journey.reviewStats.correct / journey.reviewStats.total) * 100)
      : null

  return (
    <section
      className="mb-6 rounded-xl border bg-card p-4"
      data-testid="word-journey-panel"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start justify-between gap-3 text-left"
        aria-expanded={expanded}
        data-testid="word-journey-toggle"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 shrink-0 text-primary" />
            <h2 className="text-sm font-semibold">Your journey</h2>
          </div>
          {!expanded && (
            <p className="mt-1 text-sm text-muted-foreground" data-testid="word-journey-summary">
              {buildJourneySummary(journey)}
              {journey.firstSeenArticleTitle && journey.firstSeenArticleId ? (
                <>
                  {' — '}
                  <Link
                    to={`/reading/${journey.firstSeenArticleId}`}
                    className="text-primary hover:underline"
                    data-testid="word-journey-article-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {journey.firstSeenArticleTitle}
                  </Link>
                </>
              ) : null}
            </p>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>

      <ol className={cn('space-y-3', expanded ? 'mt-4' : 'hidden')}>
        <li className="flex gap-3 text-sm">
          <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div>
            <p className="font-medium text-foreground">
              First met {formatDate(journey.firstSeenAt)}
            </p>
            <p className="text-muted-foreground">
              Added {sourceLabel(journey.firstSeenSource)}
              {journey.firstSeenArticleTitle && journey.firstSeenArticleId ? (
                <>
                  {' — '}
                  <Link
                    to={`/reading/${journey.firstSeenArticleId}`}
                    className="text-primary hover:underline"
                    data-testid="word-journey-article-link"
                  >
                    {journey.firstSeenArticleTitle}
                  </Link>
                </>
              ) : null}
            </p>
          </div>
        </li>

        {journey.encounterCount > 0 ? (
          <li className="flex gap-3 text-sm">
            <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-medium text-foreground">
                Seen {journey.encounterCount} time{journey.encounterCount === 1 ? '' : 's'}
              </p>
              {journey.lastEncounterAt ? (
                <p className="text-muted-foreground">
                  Last encounter {formatDate(journey.lastEncounterAt)}
                </p>
              ) : null}
            </div>
          </li>
        ) : null}

        {journey.aiStoryEncounterCount > 0 ? (
          <li className="flex gap-3 text-sm">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-medium text-foreground">
                Used in generated {journey.aiStoryEncounterCount === 1 ? 'story' : 'stories'}
              </p>
              <p className="text-muted-foreground">
                {journey.aiStoryEncounterCount} time{journey.aiStoryEncounterCount === 1 ? '' : 's'}
                {journey.lastAiStoryEncounterAt
                  ? ` · last ${formatDate(journey.lastAiStoryEncounterAt)}`
                  : ''}
              </p>
            </div>
          </li>
        ) : null}

        {journey.reviewStats.total > 0 ? (
          <li className="flex gap-3 text-sm">
            <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-medium text-foreground">
                Refreshed {journey.reviewStats.total} time{journey.reviewStats.total === 1 ? '' : 's'}
              </p>
              <p className="text-muted-foreground">
                {journey.reviewStats.correct} correct, {journey.reviewStats.wrong} missed
                {accuracy != null ? ` (${accuracy}% accuracy)` : ''}
              </p>
            </div>
          </li>
        ) : null}

        <li className="flex gap-3 text-sm">
          <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <p className="font-medium text-foreground">Next refresh</p>
            <p className="text-muted-foreground">
              {journey.nextReviewAt ? formatDate(journey.nextReviewAt) : 'Not scheduled yet'}
            </p>
          </div>
        </li>

        {formStats && formStats.forms.length > 0 ? (
          <li className="flex gap-3 text-sm" data-testid="word-journey-morphology">
            <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground">
                Forms {formStats.forms.filter((f) => f.met).length}/{formStats.forms.length} discovered
              </p>
              <ul className="mt-2 space-y-1.5">
                {formStats.forms.slice(0, 6).map((form) => (
                  <li key={form.surfaceForm} className="flex items-start gap-2 text-xs text-muted-foreground">
                    {form.met ? (
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600 dark:text-green-400" />
                    ) : (
                      <Circle className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-40" />
                    )}
                    <span>
                      <span className="font-medium text-foreground">{form.surfaceForm}</span>
                      {form.teachingTranslation ? (
                        <span className="text-foreground"> · {form.teachingTranslation}</span>
                      ) : null}
                      {form.met && form.encounterCount > 0 ? (
                        <span className="tabular-nums"> · {form.encounterCount}×</span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </li>
        ) : null}
      </ol>
    </section>
  )
}
